import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faChevronDown,
  faChevronRight,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";

import SecurityWrapper from "../../auth/SecurityWrapper.jsx";
import Loading from "../../layout/Loading.jsx";
import ComboBox from "../../core/ComboBox.jsx";
import useDelayedLoading from "../../../hooks/useDelayedLoading.js";
import api from "../../../services/api.js";
import {
  formatCalendarDate,
  getMonthStartDate,
  getTodayDate,
} from "../../../util/dateUtils.js";

const REPORT_TYPES = {
  DEVELOPER: "developer",
  TEAM_TASK_COMPLETION: "team_task_completion",
  QA: "qa",
};

const REPORT_TYPE_OPTIONS = [
  { value: REPORT_TYPES.DEVELOPER, label: "Developer Report" },
  {
    value: REPORT_TYPES.TEAM_TASK_COMPLETION,
    label: "Team Task Completion",
  },
  { value: REPORT_TYPES.QA, label: "QA Report" },
];

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}:${String(
    remainingSeconds
  ).padStart(2, "0")}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

const formatSprintLabel = (sprint) =>
  `${sprint.name} (${formatCalendarDate(
    sprint.start_date
  )} – ${formatCalendarDate(sprint.end_date)})`;

const getUserDisplayName = (user) =>
  `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "Unknown";

const SummaryCard = ({ label, value }) => (
  <div className="developer-report-summary-card">
    <span className="developer-report-summary-label">{label}</span>
    <span className="developer-report-summary-value">{value}</span>
  </div>
);

SummaryCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const SearchableSelect = ({
  label,
  triggerId,
  selectedLabel,
  placeholder,
  open,
  setOpen,
  data,
  selectedItems,
  valueKey,
  searchTopic,
  selectedValue,
  onSelect,
  emptyMessage,
  className = "col-md-3",
}) => (
  <div className={`form-group ${className}`}>
    <label htmlFor={triggerId}>{label}</label>
    <div
      className={`developer-report-combobox${
        open ? " developer-report-combobox--open" : ""
      }`}
    >
      <button
        type="button"
        id={triggerId}
        className="inline-select-trigger developer-report-combobox-trigger"
        onClick={() => setOpen((current) => !current)}
      >
        <span className="inline-select-text">
          {selectedLabel || placeholder}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="inline-select-chevron"
        />
      </button>
      {open && (
        <div className="inline-select-dropdown developer-report-combobox-dropdown">
          {data.length > 0 ? (
            <ComboBox
              data={data}
              selected={selectedItems}
              valueKey={valueKey}
              placeholder={`Search ${searchTopic}s...`}
              searchTopic={searchTopic}
              formatData={(item) => (
                <div className="inline-option">
                  <span>
                    {typeof item.label === "string"
                      ? item.label
                      : getUserDisplayName(item)}
                  </span>
                  {selectedValue === item[valueKey] && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="inline-option-check"
                    />
                  )}
                </div>
              )}
              handleSelect={(value) => {
                onSelect(value);
                setOpen(false);
              }}
              onRequestClose={() => setOpen(false)}
              inline={true}
            />
          ) : (
            <div className="developer-report-combobox-empty">
              {emptyMessage}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

SearchableSelect.propTypes = {
  label: PropTypes.string.isRequired,
  triggerId: PropTypes.string.isRequired,
  selectedLabel: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  valueKey: PropTypes.string.isRequired,
  searchTopic: PropTypes.string.isRequired,
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const TaskReportRow = ({ task }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="developer-report-task-row">
      <button
        type="button"
        className="developer-report-task-header"
        onClick={() => setExpanded((current) => !current)}
      >
        <FontAwesomeIcon
          icon={expanded ? faChevronDown : faChevronRight}
          className="developer-report-task-chevron"
        />
        <div className="developer-report-task-main">
          <span className="developer-report-task-link">{task.name}</span>
          <span className="developer-report-task-meta">
            {task.points_estimate} pts · Completed{" "}
            {formatCalendarDate(task.completed_at)}
            {task.qa_completed ? " · QA Completed" : ""}
          </span>
        </div>
        <span className="developer-report-task-time">
          {formatDuration(task.time_tracked_seconds?.total)}
        </span>
      </button>

      {expanded && (
        <div className="developer-report-task-detail">
          {task.qa_completed && (
            <p className="developer-report-qa-completed-badge">
              QA completed by this developer
            </p>
          )}
          <div className="developer-report-detail-grid">
            <div>
              <h4>Time Tracked</h4>
              <p>Total: {formatDuration(task.time_tracked_seconds?.total)}</p>
              <p>
                Billable: {formatDuration(task.time_tracked_seconds?.billable)}
              </p>
              <p>
                Non-Billable:{" "}
                {formatDuration(task.time_tracked_seconds?.non_billable)}
              </p>
            </div>

            <div>
              <h4>Status Durations</h4>
              <p>
                In Progress:{" "}
                {formatDuration(task.status_durations_seconds?.in_progress)}
              </p>
              <p>
                Peer Review:{" "}
                {formatDuration(task.status_durations_seconds?.peer_review)}
              </p>
              <p>
                Review: {formatDuration(task.status_durations_seconds?.review)}
              </p>
              <p>QA: {formatDuration(task.status_durations_seconds?.qa)}</p>
            </div>

            <div>
              <h4>Sprints</h4>
              {task.sprints?.length > 0 ? (
                <ul className="developer-report-sprint-list">
                  {task.sprints.map((sprint) => (
                    <li key={sprint.sprint_id}>
                      <Link to={`/sprint/${sprint.sprint_id}`}>
                        {sprint.name}
                      </Link>
                      <span>
                        {formatCalendarDate(sprint.start_date)} –{" "}
                        {formatCalendarDate(sprint.end_date)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No sprint history recorded.</p>
              )}
            </div>
          </div>

          {task.sprint_history?.length > 0 && (
            <div className="developer-report-sprint-history">
              <h4>Sprint History</h4>
              <ul>
                {task.sprint_history.map((entry, index) => (
                  <li key={`${entry.sprint_id}-${entry.created_at}-${index}`}>
                    <span className="developer-report-history-action">
                      {entry.action}
                    </span>
                    <span>{entry.sprint_name}</span>
                    <span>{formatDateTime(entry.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

TaskReportRow.propTypes = {
  task: PropTypes.shape({
    task_id: PropTypes.string,
    name: PropTypes.string,
    points_estimate: PropTypes.number,
    completed_at: PropTypes.string,
    qa_completed: PropTypes.bool,
    time_tracked_seconds: PropTypes.shape({
      total: PropTypes.number,
      billable: PropTypes.number,
      non_billable: PropTypes.number,
    }),
    status_durations_seconds: PropTypes.shape({
      in_progress: PropTypes.number,
      peer_review: PropTypes.number,
      review: PropTypes.number,
      qa: PropTypes.number,
    }),
    sprints: PropTypes.arrayOf(
      PropTypes.shape({
        sprint_id: PropTypes.string,
        name: PropTypes.string,
        start_date: PropTypes.string,
        end_date: PropTypes.string,
      })
    ),
    sprint_history: PropTypes.arrayOf(
      PropTypes.shape({
        sprint_id: PropTypes.string,
        sprint_name: PropTypes.string,
        action: PropTypes.string,
        created_at: PropTypes.string,
      })
    ),
  }).isRequired,
};

const TeamPersonSection = ({ title, rows, countKey, taskMeta }) => (
  <div className="card width-full developer-report-tasks mb-4">
    <div className="card-header">
      <h3>
        {title} ({rows?.length ?? 0})
      </h3>
    </div>
    <div className="card-body">
      {rows?.length > 0 ? (
        rows.map((row) => (
          <div key={row.user?.user_id} className="developer-report-team-person">
            <div className="developer-report-team-person-header">
              <h4>{getUserDisplayName(row.user)}</h4>
              <span>
                {row[countKey]} {row[countKey] === 1 ? "task" : "tasks"}
              </span>
            </div>
            <ul className="developer-report-team-task-list">
              {row.tasks.map((task) => (
                <li key={task.task_id}>
                  <Link to={`/task/${task.task_id}`}>{task.name}</Link>
                  <span>{taskMeta(task)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))
      ) : (
        <p className="developer-report-empty">No data for this section.</p>
      )}
    </div>
  </div>
);

TeamPersonSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      user: PropTypes.shape({
        user_id: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
      }),
      tasks: PropTypes.arrayOf(
        PropTypes.shape({
          task_id: PropTypes.string,
          name: PropTypes.string,
          points_estimate: PropTypes.number,
          completed_at: PropTypes.string,
        })
      ),
    })
  ),
  countKey: PropTypes.string.isRequired,
  taskMeta: PropTypes.func.isRequired,
};

const DeveloperReportPage = () => {
  const [reportType, setReportType] = useState(REPORT_TYPES.DEVELOPER);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedSprintId, setSelectedSprintId] = useState("");
  const [startDate, setStartDate] = useState(getMonthStartDate);
  const [endDate, setEndDate] = useState(getTodayDate);
  const [showDeveloperComboBox, setShowDeveloperComboBox] = useState(false);
  const [showSprintComboBox, setShowSprintComboBox] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSprints, setIsLoadingSprints] = useState(false);
  const [error, setError] = useState(null);

  const showLoading = useDelayedLoading(isLoading);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const results = await api.user.getUsers({ per_page: 500 });
        const sortedUsers = [...results].sort((a, b) =>
          getUserDisplayName(a).localeCompare(getUserDisplayName(b))
        );
        setUsers(sortedUsers);
      } catch (loadError) {
        console.error("Error loading users:", loadError);
        setError("Unable to load users.");
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    if (reportType !== REPORT_TYPES.TEAM_TASK_COMPLETION) {
      return;
    }
    if (!startDate || !endDate) {
      setSprints([]);
      return;
    }

    let cancelled = false;

    const loadSprints = async () => {
      setIsLoadingSprints(true);
      try {
        const results = await api.report.getSprintsInRange({
          start_date: startDate,
          end_date: endDate,
        });
        if (cancelled) return;

        const sortedSprints = [...results].sort((a, b) =>
          (a.name || "").localeCompare(b.name || "")
        );
        setSprints(sortedSprints);
        setSelectedSprintId((current) =>
          sortedSprints.some((sprint) => sprint.sprint_id === current)
            ? current
            : ""
        );
      } catch (loadError) {
        if (cancelled) return;
        console.error("Error loading sprints:", loadError);
        setError("Unable to load sprints for this date range.");
        setSprints([]);
        setSelectedSprintId("");
      } finally {
        if (!cancelled) {
          setIsLoadingSprints(false);
        }
      }
    };

    loadSprints();

    return () => {
      cancelled = true;
    };
  }, [reportType, startDate, endDate]);

  const loadReport = useCallback(async (filters) => {
    if (!filters) return;

    setIsLoading(true);
    setError(null);

    try {
      if (filters.reportType === REPORT_TYPES.DEVELOPER) {
        const results = await api.report.getDeveloperReport({
          user_id: filters.userId,
          start_date: filters.startDate,
          end_date: filters.endDate,
        });
        setReport({ type: REPORT_TYPES.DEVELOPER, data: results });
      } else if (filters.reportType === REPORT_TYPES.TEAM_TASK_COMPLETION) {
        const results = await api.report.getTeamTaskCompletionReport({
          sprint_id: filters.sprintId,
          start_date: filters.startDate,
          end_date: filters.endDate,
        });
        setReport({ type: REPORT_TYPES.TEAM_TASK_COMPLETION, data: results });
      } else {
        const results = await api.report.getQaReport({
          start_date: filters.startDate,
          end_date: filters.endDate,
        });
        setReport({
          type: REPORT_TYPES.QA,
          data: results || {
            date_range: {
              start_date: filters.startDate,
              end_date: filters.endDate,
            },
            summary: {
              qa_completed_count: 0,
              qa_created_count: 0,
              qa_people_count: 0,
              overall_time_tracked_seconds: { total: 0 },
            },
            qa_completed: [],
          },
        });
      }
    } catch (loadError) {
      console.error("Error loading report:", loadError);
      setError("Unable to load report.");
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (appliedFilters) {
      loadReport(appliedFilters);
    }
  }, [appliedFilters, loadReport]);

  const handleReportTypeChange = (event) => {
    const nextType = event.target.value;
    setReportType(nextType);
    setReport(null);
    setAppliedFilters(null);
    setError(null);
    setSelectedUserId("");
    setSelectedSprintId("");
    setShowDeveloperComboBox(false);
    setShowSprintComboBox(false);
  };

  const clearLoadedReport = () => {
    setReport(null);
    setAppliedFilters(null);
    setError(null);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (reportType === REPORT_TYPES.DEVELOPER) {
      if (!selectedUserId || !startDate || !endDate) return;
      setAppliedFilters({
        reportType,
        userId: selectedUserId,
        startDate,
        endDate,
      });
      return;
    }

    if (reportType === REPORT_TYPES.TEAM_TASK_COMPLETION) {
      if (!selectedSprintId || !startDate || !endDate) return;
      setAppliedFilters({
        reportType,
        sprintId: selectedSprintId,
        startDate,
        endDate,
      });
      return;
    }

    if (!startDate || !endDate) return;
    setAppliedFilters({
      reportType,
      startDate,
      endDate,
    });
  };

  const selectedUser = users.find((user) => user.user_id === selectedUserId);
  const selectedUserItems = selectedUser ? [selectedUser] : [];
  const selectedSprint = sprints.find(
    (sprint) => sprint.sprint_id === selectedSprintId
  );
  const sprintOptions = useMemo(
    () =>
      sprints.map((sprint) => ({
        ...sprint,
        label: formatSprintLabel(sprint),
      })),
    [sprints]
  );
  const selectedSprintItems = selectedSprint
    ? [
        {
          ...selectedSprint,
          label: formatSprintLabel(selectedSprint),
        },
      ]
    : [];

  const developerName = useMemo(() => {
    if (report?.type === REPORT_TYPES.DEVELOPER && report.data?.developer) {
      return getUserDisplayName(report.data.developer);
    }
    return getUserDisplayName(selectedUser);
  }, [report, selectedUser]);

  const canSubmit =
    reportType === REPORT_TYPES.DEVELOPER
      ? Boolean(selectedUserId && startDate && endDate)
      : reportType === REPORT_TYPES.TEAM_TASK_COMPLETION
      ? Boolean(selectedSprintId && startDate && endDate)
      : Boolean(startDate && endDate);

  const reportTypeLabel =
    REPORT_TYPE_OPTIONS.find((option) => option.value === reportType)?.label ||
    "Report";

  const isTeamTaskCompletion = reportType === REPORT_TYPES.TEAM_TASK_COMPLETION;

  const dateAndSubmitFields = (
    <>
      <div className="form-group col-md-3">
        <label htmlFor="start-date">Start Date</label>
        <input
          type="date"
          id="start-date"
          value={startDate}
          onChange={(event) => {
            setStartDate(event.target.value);
            clearLoadedReport();
          }}
        />
      </div>

      <div className="form-group col-md-3">
        <label htmlFor="end-date">End Date</label>
        <input
          type="date"
          id="end-date"
          value={endDate}
          onChange={(event) => {
            setEndDate(event.target.value);
            clearLoadedReport();
          }}
        />
      </div>

      <div className="form-group col-md-3 developer-report-submit-group">
        <label className="developer-report-submit-label" aria-hidden="true">
          &nbsp;
        </label>
        <button
          type="submit"
          className="btn btn-primary developer-report-submit-button"
          disabled={!canSubmit || isLoading}
        >
          See Report
        </button>
      </div>
    </>
  );

  return (
    <div className="developer-report-page flex-align-start width-full">
      <h1>
        <FontAwesomeIcon icon={faChartColumn} className="reports-icon" />{" "}
        Reports
      </h1>

      <form
        className="card width-full mb-4 developer-report-filters"
        onSubmit={handleSubmit}
      >
        <div className="form-row developer-report-filters-row">
          <div className="form-group col-md-3">
            <label htmlFor="report-type">Report</label>
            <select
              id="report-type"
              value={reportType}
              onChange={handleReportTypeChange}
            >
              {REPORT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {reportType === REPORT_TYPES.DEVELOPER && (
            <SearchableSelect
              label="Developer"
              triggerId="developer-combobox-trigger"
              selectedLabel={
                selectedUser ? getUserDisplayName(selectedUser) : ""
              }
              placeholder="Select developer"
              open={showDeveloperComboBox}
              setOpen={setShowDeveloperComboBox}
              data={users}
              selectedItems={selectedUserItems}
              valueKey="user_id"
              searchTopic="developer"
              selectedValue={selectedUserId}
              onSelect={(value) => {
                setSelectedUserId(value);
                clearLoadedReport();
              }}
              emptyMessage="No developers found."
            />
          )}

          {isTeamTaskCompletion && (
            <SearchableSelect
              label="Sprint"
              triggerId="sprint-combobox-trigger"
              selectedLabel={
                selectedSprint ? formatSprintLabel(selectedSprint) : ""
              }
              placeholder={
                isLoadingSprints ? "Loading sprints..." : "Select sprint"
              }
              open={showSprintComboBox}
              setOpen={setShowSprintComboBox}
              data={sprintOptions}
              selectedItems={selectedSprintItems}
              valueKey="sprint_id"
              searchTopic="sprint"
              selectedValue={selectedSprintId}
              onSelect={(value) => {
                setSelectedSprintId(value);
                clearLoadedReport();
              }}
              emptyMessage={
                isLoadingSprints
                  ? "Loading sprints..."
                  : "No sprints active in this date range."
              }
              className="col-md-9"
            />
          )}

          {!isTeamTaskCompletion && dateAndSubmitFields}
        </div>

        {isTeamTaskCompletion && (
          <div className="form-row developer-report-filters-row">
            {dateAndSubmitFields}
          </div>
        )}
      </form>

      {error && <div className="developer-report-error">{error}</div>}

      {showLoading && <Loading />}

      {!showLoading && !report && !error && (
        <p className="developer-report-empty">
          Select filters and click See Report to load{" "}
          {reportTypeLabel.toLowerCase()} data.
        </p>
      )}

      {!showLoading &&
        report?.type === REPORT_TYPES.DEVELOPER &&
        report.data && (
          <>
            <div className="developer-report-summary width-full mb-4">
              <h2>{developerName}</h2>
              <p className="developer-report-date-range">
                Completed tasks from{" "}
                {formatCalendarDate(report.data.date_range?.start_date)} to{" "}
                {formatCalendarDate(report.data.date_range?.end_date)}
              </p>

              <div className="developer-report-summary-grid">
                <SummaryCard
                  label="Velocity Completed"
                  value={report.data.summary?.velocity_completed ?? 0}
                />
                <SummaryCard
                  label="Tasks Completed"
                  value={report.data.summary?.tasks_completed_count ?? 0}
                />
                <SummaryCard
                  label="Overall Time Tracked"
                  value={formatDuration(
                    report.data.summary?.overall_time_tracked_seconds?.total
                  )}
                />
                <SummaryCard
                  label="Completed Tasks Time"
                  value={formatDuration(
                    report.data.summary?.time_tracked_seconds?.total
                  )}
                />
                <SummaryCard
                  label="Billable Time"
                  value={formatDuration(
                    report.data.summary?.time_tracked_seconds?.billable
                  )}
                />
                <SummaryCard
                  label="Non-Billable Time"
                  value={formatDuration(
                    report.data.summary?.time_tracked_seconds?.non_billable
                  )}
                />
              </div>
            </div>

            <div className="card width-full developer-report-tasks">
              <div className="card-header">
                <h3>Completed Tasks ({report.data.tasks?.length ?? 0})</h3>
              </div>
              <div className="card-body">
                {report.data.tasks?.length > 0 ? (
                  report.data.tasks.map((task) => (
                    <TaskReportRow key={task.task_id} task={task} />
                  ))
                ) : (
                  <p className="developer-report-empty">
                    No completed tasks found for this developer and date range.
                  </p>
                )}
              </div>
            </div>
          </>
        )}

      {!showLoading &&
        report?.type === REPORT_TYPES.TEAM_TASK_COMPLETION &&
        report.data && (
          <>
            <div className="developer-report-summary width-full mb-4">
              <h2>{report.data.sprint?.name || "Team Task Completion"}</h2>
              <p className="developer-report-date-range">
                Completed tasks from{" "}
                {formatCalendarDate(report.data.date_range?.start_date)} to{" "}
                {formatCalendarDate(report.data.date_range?.end_date)}
              </p>

              <div className="developer-report-summary-grid">
                <SummaryCard
                  label="Tasks Completed"
                  value={report.data.summary?.tasks_completed_count ?? 0}
                />
                <SummaryCard
                  label="People with Completions"
                  value={report.data.summary?.assignees_with_completions ?? 0}
                />
                <SummaryCard
                  label="Overall Time Tracked"
                  value={formatDuration(
                    report.data.summary?.overall_time_tracked_seconds?.total
                  )}
                />
              </div>
            </div>

            <TeamPersonSection
              title="Completions by Assignee"
              rows={report.data.completions_by_assignee}
              countKey="tasks_completed_count"
              taskMeta={(task) =>
                `${
                  task.points_estimate || 0
                } pts · Completed ${formatCalendarDate(task.completed_at)}`
              }
            />
          </>
        )}

      {!showLoading && report?.type === REPORT_TYPES.QA && report.data && (
        <>
          <div className="developer-report-summary width-full mb-4">
            <h2>QA Report</h2>
            <p className="developer-report-date-range">
              QA activity from{" "}
              {formatCalendarDate(report.data.date_range?.start_date)} to{" "}
              {formatCalendarDate(report.data.date_range?.end_date)}
            </p>

            <div className="developer-report-summary-grid">
              <SummaryCard
                label="QA Completed"
                value={report.data.summary?.qa_completed_count ?? 0}
              />
              <SummaryCard
                label="QA Created"
                value={report.data.summary?.qa_created_count ?? 0}
              />
              <SummaryCard
                label="QA People"
                value={report.data.summary?.qa_people_count ?? 0}
              />
              <SummaryCard
                label="Overall Time Tracked"
                value={formatDuration(
                  report.data.summary?.overall_time_tracked_seconds?.total
                )}
              />
            </div>
          </div>

          <TeamPersonSection
            title="QA Completed"
            rows={report.data.qa_completed}
            countKey="qa_completed_count"
            taskMeta={(task) =>
              `Completed ${formatCalendarDate(task.completed_at)}`
            }
          />
        </>
      )}
    </div>
  );
};

const DeveloperReportPageWithSecurity = () => (
  <SecurityWrapper requiredRoles={["super-admin"]} redirect>
    <DeveloperReportPage />
  </SecurityWrapper>
);

export default DeveloperReportPageWithSecurity;
